import { useCallback, useEffect, useState } from 'react';
import { Variable } from 'src/api/types';
import _ from 'lodash';
import { StandardVariableValues } from './types';
import { genStandardVariableValues } from './util';
import { useCache } from 'src/graphql';
import useWorkspaceVariables from 'src/hooks/useWorkspaceVariables';
import useAssessmentVariables from './useAssessmentVariables';

type UseVariables = (a: { assessmentId?: string; wvOverride?: boolean }) => {
  variables: Variable[] | null;
  isLoading: boolean;
  containsUndefinedVariableFromFormula: (expression: string) => boolean;
  standardVariableValues: StandardVariableValues | null;
  invalidate: (invalidateAssessment?: boolean) => Promise<void>;
};

const useVariables: UseVariables = ({ assessmentId, wvOverride }) => {
  const [variables, setVariables] = useState<Variable[] | null>(null);
  const [standardVariableValues, setStandardVariableValues] = useState<StandardVariableValues | null>(null);
  const { variables: wVariables, isLoading: isWVarsLoading } = useWorkspaceVariables();
  const { assessmentVariables: aVars, isAssessmentVariablesLoading } = useAssessmentVariables({ assessmentId });
  const { refetchQuery } = useCache();

  const invalidate = async () => {
    await refetchQuery({ include: ['workspaceVariables', 'assessmentVariables'] });
  };

  useEffect(() => {
    if (!isAssessmentVariablesLoading && !isWVarsLoading) {
      const assessmentVariables = aVars || [];

      const allVariables = _.sortBy([...assessmentVariables, ...wVariables], (el) =>
        el.alias ? el.alias.toLowerCase() : el.name.toLowerCase(),
      );

      setVariables(allVariables);

      const gen = genStandardVariableValues(wVariables);
      setStandardVariableValues(gen);
    }
  }, [aVars, isAssessmentVariablesLoading, isWVarsLoading, wvOverride, wVariables]);

  const containsUndefinedVariableFromFormula = useCallback(
    (expression: string) => {
      const variableIds = expression.match(/#\d+/g);
      return !!variableIds?.some((id) => variables?.find((v) => v.id === parseInt(id.slice(1)))?.value === null);
    },
    [variables],
  );

  return {
    variables,
    isLoading: isAssessmentVariablesLoading || isWVarsLoading || !standardVariableValues,
    containsUndefinedVariableFromFormula,
    standardVariableValues,
    invalidate,
  };
};

export default useVariables;
